// Roles.jsx
import React, { useEffect, useState } from 'react';
import api from '../api';
import { hasPerm } from '../utils/perm';

function RoleRow({ role, allPerms, onSavePerms, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(role.name);
  const [desc, setDesc] = useState(role.description || '');
  const [checked, setChecked] = useState(new Set(role.permissions || []));

  useEffect(() => {
    setName(role.name);
    setDesc(role.description || '');
    setChecked(new Set(role.permissions || []));
  }, [role.id, role.name, role.description, role.permissions]);

  const canUpdate = hasPerm('roles:update');
  const canDelete = hasPerm('roles:delete');
  const canRename = canUpdate && !role.built_in; // built-ins: keep name/desc immutable

  const toggle = (p) => {
    const next = new Set(checked);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setChecked(next);
  };

  const isDirty = () => {
    const cur = new Set(role.permissions || []);
    if (cur.size !== checked.size) return true;
    for (const p of checked) if (!cur.has(p)) return true;
    return false;
  };

  return (
    <tr>
      <td data-label="Name" style={{ minWidth: 180 }}>
        {editing ? (
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canRename}
          />
        ) : (
          role.name
        )}
      </td>

      <td data-label="Description">
        {editing ? (
          <input
            className="input"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            disabled={!canRename}
          />
        ) : (
          role.description || '—'
        )}
      </td>

      <td data-label="Permissions">
        <div className="perm-list">
          {allPerms.map((p) => (
            <label key={p} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={checked.has(p)}
                onChange={() => toggle(p)}
                disabled={!canUpdate}
              />
              <span className="mono" style={{ fontSize: 12 }}>{p}</span>
            </label>
          ))}
        </div>

        <div className="perm-actions">
          <button
            className="btn small"
            disabled={!canUpdate || !isDirty()}
            onClick={() => onSavePerms(role.id, Array.from(checked))}
            title={!canUpdate ? 'No permission' : (!isDirty() ? 'No changes to save' : 'Save permissions')}
          >
            Save
          </button>

          {canRename && (
            editing ? (
              <>
                <button
                  className="btn small"
                  onClick={() => {
                    onRename(role.id, name, desc, Array.from(checked));
                    setEditing(false);
                  }}
                >
                  Save name/desc
                </button>
                <button className="btn small outline" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="btn small" onClick={() => setEditing(true)}>
                Edit name/desc
              </button>
            )
          )}

          {!role.built_in && canDelete && (
            <button
              className="btn small danger"
              onClick={() => onDelete(role.id)}
              title="Delete role"
            >
              Delete
            </button>
          )}
        </div>
      </td>

      <td data-label="Users" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {typeof role.users_count === 'number' && (
          <span className="mono" style={{ color: 'var(--muted,#475569)', fontSize: 12 }}>
            {role.users_count} user{role.users_count === 1 ? '' : 's'}
          </span>
        )}
      </td>
    </tr>
  );
}

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [infoOpen, setInfoOpen] = useState(null);
  const canCreate = hasPerm('roles:create');

  async function load() {
    try {
      const r = await api.get('/roles');
      setRoles(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.warn('Failed to load roles', e);
      setRoles([]);
    }

    try {
      const perms = await api.get('/roles/all/perms');
      setAllPerms(Array.isArray(perms.data) ? perms.data : []);
    } catch (e) {
      // fallback to /permissions if /all/perms is missing
      try {
        const recs = await api.get('/roles/permissions');
        setAllPerms(Array.isArray(recs.data) ? recs.data.map((x) => x.name) : []);
      } catch (e2) {
        console.warn('Failed to load perms', e2);
        setAllPerms([]);
      }
    }
  }

  useEffect(() => { load(); }, []);

  async function createRole(e) {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post('/roles', { name: name.trim(), description: desc.trim() || null });
    setName(''); setDesc('');
    await load();
  }

  // Save permissions via PUT /roles/:id
  async function onSavePerms(roleId, perms) {
    await api.put(`/roles/${roleId}`, { permissions: perms });
    await load();
  }

  // Rename/description via PUT /roles/:id (include current perms to avoid accidental changes)
  async function onRename(id, newName, newDesc, currentPerms) {
    await api.put(`/roles/${id}`, {
      name: newName,
      description: (newDesc || '').trim(),
      permissions: Array.isArray(currentPerms) ? currentPerms : undefined,
    });
    await load();
  }

  async function onDelete(id) {
    if (!window.confirm('Delete this role? Only roles not in use can be deleted.')) return;
    await api.delete(`/roles/${id}`);
    await load();
  }

  return (
    <div className="page" style={{ background: 'var(--bg,#f8fafc)', color: 'var(--fg,#0b1220)' }}>
      <div className="row between center header-row" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Roles</h2>
          <button
            type="button"
            className="btn btn-sm secondary"
            onClick={() => setInfoOpen('roles')}
            style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
            title="Learn more about Roles"
          >
            ℹ️
          </button>
        </div>

        {canCreate && (
          <form onSubmit={createRole} className="row role-form" style={{ alignItems: 'flex-end', marginLeft: 'auto' }}>
            <div className="col">
              <label>Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="col">
              <label>Description</label>
              <input className="input" value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>
            <button className="btn" type="submit">Create</button>
          </form>
        )}
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--muted,#475569)' }}>
        {allPerms.length === 0 && 'Permission list unavailable (limited editing).'}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 180 }}>Name</th>
              <th style={{ width: 320 }}>Description</th>
              <th>Permissions</th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <RoleRow
                key={r.id}
                role={r}
                allPerms={allPerms}
                onSavePerms={onSavePerms}
                onRename={onRename}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Info Modal */}
      {infoOpen === 'roles' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8, background: 'var(--card,#fff)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Roles Management</div>
              <button
                className="btn outline small"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg,#0b1220)' }}>
              <p><strong>Roles</strong> are predefined sets of permissions that you can assign to users. Each role determines what actions users are allowed to perform in the system.</p>
              <p><strong>Role Management Features:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li><strong>Create Role:</strong> Define new custom roles with specific permission sets</li>
                <li><strong>Edit Permissions:</strong> Add or remove permissions from existing roles</li>
                <li><strong>Rename/Describe:</strong> Update role names and descriptions (except built-in roles)</li>
                <li><strong>Delete Role:</strong> Remove roles that are no longer in use</li>
              </ul>
              <p><strong>Permission Categories:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li><strong>Users:</strong> Create, update, and deactivate user accounts</li>
                <li><strong>Roles:</strong> Create, update, and delete roles and permissions</li>
                <li><strong>Products:</strong> View, create, edit, and delete products</li>
                <li><strong>Customers:</strong> Manage customer profiles and information</li>
                <li><strong>Sales:</strong> Create and process sales transactions</li>
                <li><strong>Reports:</strong> Access and view business reports and analytics</li>
              </ul>
              <p><strong>Built-in Roles:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li><strong>Owner:</strong> Full system access and user management</li>
                <li><strong>Manager:</strong> Can view reports and manage operations</li>
                <li><strong>Cashier:</strong> Can process sales and returns</li>
                <li><strong>Viewer:</strong> Read-only access to view reports and data</li>
                <li><strong>Auditor:</strong> Can view and audit Invoices and Credit Notes</li>
              </ul>
              <p><strong>Best Practices:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Follow principle of least privilege - assign only necessary permissions</li>
                <li>Create custom roles for specific job functions</li>
                <li>Regularly review role assignments and permissions</li>
                <li>Document what each custom role is used for</li>
                <li>Cannot delete built-in roles or roles with active users</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Card / table */
        .card {
          background: var(--card,#fff);
          color: var(--fg,#0b1220);
          border:1px solid var(--hairline,#e5e7eb);
          border-radius:12px;
          padding:12px;
        }
        table { border-collapse: collapse; width:100%; }
        .table thead th {
          color: var(--muted,#475569);
          font-weight:600;
          text-align:left;
          border-bottom:1px solid var(--hairline,#e5e7eb);
          padding:10px 12px;
        }
        .table tbody td {
          border-bottom:1px solid var(--hairline,#e5e7eb);
          padding:10px 12px;
          vertical-align:top;
        }
        .perm-list {
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          max-width:760px;
        }
        .perm-actions {
          margin-top:8px;
          display:flex;
          gap:8px;
          flex-wrap:wrap;
        }
        .role-form { display:flex; gap:10px; }
        .role-form .col { min-width:180px; }

        /* Inputs */
        .input {
          width:100%;
          padding:8px 10px;
          border:1px solid var(--hairline,#e5e7eb);
          border-radius:8px;
          background: var(--card,#fff);
          color: var(--fg,#0b1220);
          outline:none;
        }
        .input:focus {
          border-color: var(--accent,#7c3aed);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent,#7c3aed) 25%, transparent);
        }

        /* Buttons */
        .btn {
          padding:8px 12px;
          border-radius:8px;
          border:1px solid var(--hairline,#e5e7eb);
          background: var(--link,#2563eb);
          color:#fff;
          cursor:pointer;
        }
        .btn.small { padding:6px 10px; font-size:12px; }
        .btn.outline { background:transparent; color: var(--fg,#0b1220); }
        .btn.outline:hover { background: color-mix(in srgb, var(--fg,#0b1220) 8%, transparent); }
        .btn.danger { background: var(--danger,#b91c1c); border-color: color-mix(in srgb, var(--danger,#b91c1c) 70%, transparent); }
        .btn:disabled { opacity:0.6; cursor:not-allowed; }

        /* Page padding last so it inherits bg/fg */
        .page { padding:16px; }
        
        /* Mobile adjustments */
        @media (max-width: 768px) {
          .header-row { flex-direction:column; align-items:flex-start; gap:10px; }
          .role-form { width:100%; flex-wrap:wrap; gap:10px; margin-left:0 !important; }
          .role-form .col { width:100%; }
          .role-form button { width:100%; }
          .table { overflow-x: visible; }
          table { min-width:0; }
          thead { display:none; }
          .table tbody tr { display:block; border-bottom:1px solid var(--hairline,#e5e7eb); padding:10px; }
          .table tbody td { display:flex; flex-direction:column; align-items:flex-start; gap:6px; border:none; padding:6px 2px; word-break:break-word; }
          .table tbody td::before { content: attr(data-label); font-weight:600; color:var(--muted,#475569); margin-bottom:2px; white-space:nowrap; }
          .perm-list { display:grid; grid-template-columns:1fr; gap:8px; max-width:100%; }
          .perm-actions { flex-direction:column; align-items:flex-start; width:100%; margin-top:8px; }
          .perm-actions .btn { width:100%; text-align:center; }
        }
      `}</style>
    </div>
  );
}
